// A catalogue product's supply note and what is In Stock, shown under the
// product wherever somebody picks it: New Hardware Request, the PMO's review
// of a request, SOW detail's Add Hardware, a PO's Add Line, and the product's
// own detail in the Product Catalog. The rules (age bands, what counts as
// stock) are in utils/productNotes.js and src/api/routes/productNotes.js.
//
// Holders of product-notes:write can log a new note or withdraw the current
// one in place. Never rendered in the client ordering portal.
import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { noteAge, isOutOfDate, summariseStock, describeLocations, STALE_OVER_DAYS } from '../../utils/productNotes';

const BADGE = {
  fresh:   'bg-emerald-100 text-emerald-700',
  ageing:  'bg-amber-100 text-amber-800',
  stale:   'bg-red-100 text-red-700',
  expired: 'bg-red-100 text-red-700',
};

function NoteMeta({ note }) {
  return (
    <div className="text-[8px] text-gray-500">
      Logged by {note.created_by_name || 'unknown'} on {formatDate(note.created_at)}
      {note.valid_until && <> · valid until {formatDate(note.valid_until)}</>}
    </div>
  );
}

function History({ rows }) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  return (
    <div className="mt-1.5">
      <button type="button" onClick={() => setOpen(o => !o)} className="text-[8px] font-semibold text-gold hover:underline">
        {open ? '▾' : '▸'} History ({rows.length} earlier)
      </button>
      {open && (
        <div className="mt-1 border-l-2 border-gray-200 pl-2 space-y-1">
          {rows.map(r => (
            <div key={r.id} className="text-[8px] text-gray-500">
              <span className="text-gray-400">{formatDate(r.created_at)}, {r.created_by_name || 'unknown'}: </span>
              {r.kind === 'withdrawn'
                ? <span className="italic">withdrew the note below, so nobody was shown it from then on</span>
                : <>{r.body}{r.valid_until && <span className="text-gray-400"> (valid until {formatDate(r.valid_until)})</span>}</>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteForm({ productId, onSaved, onCancel }) {
  const [body, setBody] = useState('');
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try {
      onSaved(await api.post(`product-notes/${productId}`, { body, valid_until: until || null }));
    } catch (e) {
      setError(e.message || 'The note was not saved.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1.5 bg-white border border-gray-200 rounded p-2 space-y-1.5" data-testid="supply-note-form">
      <textarea className="form-textarea text-[9px]" rows="2" autoFocus value={body} onChange={e => setBody(e.target.value)}
        placeholder="What the supplier told you, e.g. 4 left at Midwich, next stock due w/c 9 November, 3 weeks after that" />
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[8px] font-bold text-gray-500 uppercase">Valid until <span className="font-normal normal-case text-gray-400">(optional)</span></label>
        <input type="date" className="form-input text-[9px] w-auto" value={until} onChange={e => setUntil(e.target.value)} />
        <div className="ml-auto flex gap-1.5">
          <button type="button" onClick={onCancel} className="px-2 py-0.5 text-[9px] border border-gray-200 rounded bg-white">Cancel</button>
          <button type="button" onClick={save} disabled={busy || !body.trim()} className="px-2 py-0.5 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-40">
            {busy ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </div>
      {error && <div className="text-[8px] text-red-700">{error}</div>}
    </div>
  );
}

/**
 * productId   the catalogue product to show
 * showStock   draw the In Stock lines (Add Hardware has its own stock panel)
 * canWrite    the viewer holds product-notes:write
 * requesting  the viewer is raising a request, so the stock lines say to
 *             request as normal (stock is information only, not an allocation)
 * onCurrent   told the current note (or null) whenever it loads or changes
 * onStock     told how many unallocated units are in stock, any condition
 */
export default function ProductSupplyNote({ productId, showStock = true, canWrite = false, requesting = false, onCurrent, onStock }) {
  const [data, setData] = useState(null);
  const [writing, setWriting] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setData(null); setWriting(false); setConfirmWithdraw(false); setError('');
    if (!productId) { onCurrent?.(null); onStock?.(0); return undefined; }
    api.get(`product-notes/${productId}`)
      .then(d => {
        if (!live) return;
        setData(d); onCurrent?.(d?.current || null);
        onStock?.((d?.stock || []).reduce((n, r) => n + Number(r.qty || 0), 0));
      })
      .catch(() => { if (live) { onCurrent?.(null); onStock?.(0); } });
    return () => { live = false; };
  // onCurrent and onStock are setters the caller passes; reloading on its identity would refetch on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  if (!productId || !data) return null;

  const applyNotes = (d) => {
    setData(prev => ({ ...prev, current: d.current, history: d.history }));
    onCurrent?.(d.current || null);
    setWriting(false); setConfirmWithdraw(false); setError('');
  };
  const withdraw = async () => {
    setError('');
    try {
      applyNotes(await api.post(`product-notes/${productId}`, { withdraw: true }));
    } catch (e) {
      setError(e.message || 'The note was not withdrawn.');
    }
  };

  const stock = summariseStock(data.stock || []);
  const current = data.current;
  const earlier = current ? data.history.slice(1) : data.history;
  const age = current ? noteAge(current) : null;
  const old = age && isOutOfDate(age);

  return (
    <div className="space-y-1.5" data-testid="product-supply-note">
      {showStock && stock.newQty > 0 && (
        <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-[9px] text-emerald-800">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
          <span><span className="font-bold">{stock.newQty} in stock now</span>, not allocated: {describeLocations(stock.newLocations)}{requesting && <> · <span className="font-semibold">request like normal</span></>}</span>
        </div>
      )}
      {showStock && stock.others.map(o => (
        <div key={o.condition} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-[9px] text-gray-600">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          <span>Also in stock, not new: <span className="font-bold">{o.qty} {o.label}</span>, not allocated: {describeLocations(o.locations)}{requesting && <> · <span className="font-semibold">request like normal</span></>}</span>
        </div>
      ))}

      {current ? (
        <div className={`rounded border px-2 py-1.5 ${old ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`text-[8px] font-bold uppercase whitespace-nowrap ${old ? 'text-gray-500' : 'text-amber-700'}`}>PMO supply note</span>
            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${BADGE[age.state]}`}>{age.label}</span>
            {canWrite && !writing && (
              <span className="ml-auto flex gap-2 whitespace-nowrap">
                <button type="button" onClick={() => { setWriting(true); setConfirmWithdraw(false); }} className="text-[8px] font-semibold text-navy hover:underline">New note</button>
                <button type="button" onClick={() => setConfirmWithdraw(true)} className="text-[8px] font-semibold text-red-600 hover:underline">Withdraw</button>
              </span>
            )}
          </div>
          <div className={`text-[9.5px] my-1 whitespace-pre-wrap ${old ? 'text-gray-500' : 'text-gray-800'}`}>{current.body}</div>
          {old && (
            <div className="text-[8px] font-semibold text-red-700 mb-0.5">
              {age.state === 'expired' ? 'Past its valid until date.' : `Over ${STALE_OVER_DAYS} days old.`} It may no longer be accurate, so check with the PMO before relying on it.
            </div>
          )}
          <NoteMeta note={current} />
          {confirmWithdraw && (
            <div className="mt-1.5 flex items-center gap-2 bg-white border border-red-200 rounded px-2 py-1">
              <span className="text-[8.5px] text-gray-700">Withdraw this note? Nobody will be shown it again, and it stays in the history.</span>
              <span className="ml-auto flex gap-1.5 flex-shrink-0">
                <button type="button" onClick={() => setConfirmWithdraw(false)} className="px-2 py-0.5 text-[9px] border border-gray-200 rounded bg-white">Keep showing it</button>
                <button type="button" onClick={withdraw} className="px-2 py-0.5 text-[9px] font-semibold bg-red-600 text-white rounded">Withdraw note</button>
              </span>
            </div>
          )}
          {writing && <NoteForm productId={productId} onSaved={applyNotes} onCancel={() => setWriting(false)} />}
          {error && <div className="text-[8px] text-red-700 mt-1">{error}</div>}
          <History rows={earlier} />
        </div>
      ) : canWrite && (
        <div className="rounded border border-dashed border-gray-200 px-2 py-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[8px] font-bold uppercase text-gray-400">No supply note</span>
            {!writing && (
              <button type="button" onClick={() => setWriting(true)} className="ml-auto text-[8px] font-semibold text-navy hover:underline">Add note</button>
            )}
          </div>
          {writing && <NoteForm productId={productId} onSaved={applyNotes} onCancel={() => setWriting(false)} />}
          <History rows={earlier} />
        </div>
      )}
    </div>
  );
}
